import { z } from "zod";

/**
 * Canonical entry-form validation schema. The form lives in several places
 * (legacy attorney UI, v1 v2-shim, the new v2 entry form); each local
 * `schema.ts` re-exports from here so the rules can't drift between copies.
 *
 * Rules mirror the v2 backend's loosened save-time contract
 * (`POST /v2/entries`, `PATCH /v2/entries/:id`):
 *
 *  Required:
 *    - date (must be a valid Date; future dates are allowed)
 *    - duration (≥ 0; the action layer converts to `unitsSeconds`)
 *    - description (may be empty; not the "Please fill in the narrative." sentinel)
 *
 *  Optional (form passes "", action layer converts to null):
 *    - matter / matter_id / task / activity
 *
 * Per-firm task/activity requirements have moved to the export pipeline on
 * the backend — do not re-add them here.
 */
export const timeEntrySchema = z.object({
  date: z.date({ message: "Date is required" }),
  matter_id: z.string(),
  matter: z.string(),
  task: z.string(),
  activity: z.string(),
  duration: z.number().nonnegative("Duration cannot be negative"),
  description: z
    .string()
    .refine((s) => s !== "Please fill in the narrative.", {
      message: "Please write a real narrative",
    }),
});

export type TimeEntryFormValues = z.infer<typeof timeEntrySchema>;

export const defaultFormValues: TimeEntryFormValues = {
  date: new Date(),
  matter_id: "",
  matter: "",
  task: "",
  activity: "",
  duration: 0,
  description: "",
};
